import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { PreCheckWarningList, bodyForCode, titleForCode } from './pre-check-warnings'

type Props = {
  open: boolean
  codes: string[]
  onClose: () => void
}

/**
 * Information modal opened when the user clicks a per-row warning badge on the
 * upload page. Explains what the pre-check flagged. Single "Okej" close button.
 *
 * Single code: header carries that code's title; description carries its body.
 * Multiple codes: header carries a plural title; stacked sections below render
 * each code's title + body via PreCheckWarningList.
 */
export const PreCheckWarningInfoDialog = ({ open, codes, onClose }: Props) => {
  const { t } = useTranslation()
  const uniqueCodes = Array.from(new Set(codes))
  const isMultiple = uniqueCodes.length > 1
  const primaryCode = uniqueCodes[0] ?? ''

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" hideCloseButton>
        <DialogHeader>
          <DialogTitle>
            {isMultiple ? t('preCheckInfo.multiple.title') : titleForCode(primaryCode, t)}
          </DialogTitle>
          {!isMultiple && (
            <DialogDescription>{bodyForCode(primaryCode, t)}</DialogDescription>
          )}
        </DialogHeader>

        {isMultiple && <PreCheckWarningList codes={uniqueCodes} />}

        <DialogFooter>
          <Button onClick={onClose}>{t('preCheckInfo.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
